import {
	isDraftServicePeriod,
	resolveFinancialCurrency,
	toApiSortOrder,
	UnitPayoutStatus,
} from "@/features/financialShared";
import type {
	ChargesInvoiceDetailsBody,
	ChargesServicePeriodUnitsResponse,
	GetChargesServicePeriodUnitsArgs,
	UpdateChargesUnitInvoiceBody,
} from "./chargesManagement.api.types";
import {
	mapChargesPaymentMethodLabel,
	mapChargesPaymentMethodToApi,
	mapChargesPaymentStatusToApi,
	mapChargesStatusFromApi,
	mapChargesUnitStatusFromApi,
} from "./chargesManagement.api.types";
import type {
	ChargeLineItem,
	ChargesInvoiceDetails,
	ChargesOwnerDetailRow,
	ChargesPeriodUnitsHeader,
	ChargesUnitDetail,
	ChargesUnitListItem,
	ChargesUnitOwner,
} from "./chargesManagement.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function toDateInputValue(raw: string): string {
	const trimmed = raw.trim();
	if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function parseOptionalMoney(raw: unknown): number | null {
	if (raw == null || raw === "") return null;
	if (typeof raw === "number" && Number.isFinite(raw)) return raw;
	const digits = String(raw).replace(/[^\d.-]/g, "");
	if (!digits) return null;
	const parsed = Number(digits);
	return Number.isFinite(parsed) ? parsed : null;
}

function parseMoney(raw: unknown): number {
	if (typeof raw === "number" && Number.isFinite(raw)) return raw;
	const digits = String(raw ?? "").replace(/[^\d.-]/g, "");
	if (!digits) return 0;
	const parsed = Number(digits);
	return Number.isFinite(parsed) ? parsed : 0;
}

function parseNamedPerson(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name ?? row.fullName).trim();
}

function initialsFromName(name: string): string {
	return name
		.replace(/\b(mr|ms|mrs|dr)\b\.?/gi, "")
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

export function parseChargesPeriodUnitsHeader(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): ChargesPeriodUnitsHeader {
	const row = asRecord(raw) ?? {};
	return {
		propertyId: asString(row.propertyId, fallback.propertyId),
		propertyName: asString(row.propertyName).trim() || "Untitled property",
		servicePeriodId: asString(
			row.servicePeriodId ?? row.periodId,
			fallback.periodId,
		),
		periodLabel: asString(row.periodLabel).trim() || "Service period",
		periodStatus: mapChargesStatusFromApi(row.periodStatus),
		uploadedAt: asString(row.uploadedAt).trim() || null,
		uploadedByName: parseNamedPerson(row.uploadedByName ?? row.uploadedBy),
		hasUploadedData: asBoolean(row.hasUploadedData, false),
		chargesLocked: asBoolean(row.chargesLocked, false),
		canUpload: asBoolean(row.canUpload, false),
		canEdit: asBoolean(row.canEdit, false),
	};
}

function parseUnitListItem(
	raw: unknown,
	fallbackCurrency?: string,
): ChargesUnitListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id ?? row.unitId).trim();
	if (!id) return null;
	const owner = asRecord(row.owner);
	const ownerName =
		parseNamedPerson(row.owner) || asString(row.ownerName).trim() || "—";
	const ownerEmail =
		asString(owner?.email).trim() || asString(row.ownerEmail).trim();
	const status = mapChargesUnitStatusFromApi(row.status ?? row.statusLabel);
	const remainingDisplay = asString(row.remainingDisplay).trim();
	const isFullyPaid =
		status === UnitPayoutStatus.SETTLED ||
		remainingDisplay.toLowerCase() === "fully paid";
	const partialDisplay = asString(row.partialPaymentDisplay).trim();
	const partialPayment =
		!partialDisplay || partialDisplay === "—"
			? null
			: parseMoney(partialDisplay);
	const paymentLabel =
		asString(row.paymentMethodLabel).trim() ||
		mapChargesPaymentMethodLabel(row.paymentMethod);
	const actions = asRecord(row.actions) ?? {};
	return {
		id,
		unitCode: asString(row.unitNumber ?? row.unitCode).trim() || "—",
		ownerName,
		ownerEmail,
		invoiceNumber: asString(row.invoiceNumber).trim() || "—",
		amount: parseMoney(row.amount),
		tax: parseMoney(row.tax),
		total: parseMoney(row.total ?? row.amount),
		partialPayment,
		remainingLabel: remainingDisplay || "—",
		isFullyPaid,
		paymentMethod: paymentLabel || "—",
		status,
		statusLabel: asString(row.statusLabel).trim() || status,
		currency: resolveFinancialCurrency(row.currency, fallbackCurrency),
		canView: asBoolean(actions.canView, true),
		canEdit: asBoolean(actions.canEdit, false),
	};
}

export function parseChargesServicePeriodUnitsResponse(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): ChargesServicePeriodUnitsResponse {
	const data = unwrapData(raw);
	const listCurrency = resolveFinancialCurrency(data.currency);
	const items = Array.isArray(data.items)
		? data.items
				.map((item) => parseUnitListItem(item, listCurrency))
				.filter((item): item is ChargesUnitListItem => Boolean(item))
		: [];
	const stats = asRecord(data.stats) ?? {};
	return {
		header: parseChargesPeriodUnitsHeader(data.header, fallback),
		currency: listCurrency,
		stats: {
			totalUnits: asNumber(stats.totalUnits, items.length),
			settled: asNumber(stats.settled),
			outstanding: asNumber(stats.outstanding),
			pastDueOrPartial: asNumber(stats.pastDueOrPartial),
		},
		items,
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
		emptyMessage: asString(data.emptyMessage).trim() || "No units found.",
	};
}

export function buildChargesServicePeriodUnitsParams(
	args: GetChargesServicePeriodUnitsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
		status: args.status?.trim() || "ALL",
		paymentMethod: args.paymentMethod?.trim() || "ALL",
		sortBy: args.sortBy?.trim() || "UNIT",
		sortOrder: toApiSortOrder(args.sortOrder),
	};
	if (args.search?.trim()) params.search = args.search.trim();
	return params;
}

function parseOwners(raw: unknown): ChargesUnitOwner[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			const row = asRecord(item);
			if (!row) return null;
			const fullName = asString(row.fullName ?? row.name).trim();
			return {
				fullName,
				email: asString(row.email),
				phone: asString(row.phone),
				initials:
					asString(row.initials).trim() || initialsFromName(fullName) || "—",
				propertyName: asString(row.propertyName),
				unitNumber: asString(row.unitNumber ?? row.unit),
				isPrimary: asBoolean(row.isPrimary, false),
				roleLabel: asString(row.roleLabel),
			} satisfies ChargesUnitOwner;
		})
		.filter((item): item is ChargesUnitOwner => Boolean(item));
}

function parseOwnerDetails(raw: unknown): ChargesOwnerDetailRow[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			const row = asRecord(item);
			if (!row) return null;
			return {
				ownerName: asString(row.ownerName),
				unitNumber: asString(row.unitNumber),
				invoiceNumber: asString(row.invoiceNumber),
				startDate: asString(row.startDate),
				endDate: asString(row.endDate),
				dueDate: asString(row.dueDate),
			} satisfies ChargesOwnerDetailRow;
		})
		.filter((item): item is ChargesOwnerDetailRow => Boolean(item));
}

function parseLineItems(
	raw: unknown,
	fallbackCurrency: string,
): ChargeLineItem[] {
	const source = Array.isArray(raw)
		? raw
		: Array.isArray(asRecord(raw)?.items)
			? (asRecord(raw)?.items as unknown[])
			: [];
	return source
		.map((item, index) => {
			const row = asRecord(item);
			if (!row) return null;
			const amount = parseMoney(row.amount);
			const tax = parseMoney(row.tax);
			return {
				id: asString(row.id).trim() || `line-${index}`,
				category: asString(row.category),
				description: asString(row.description),
				amount,
				tax,
				total: parseMoney(row.totalIncTax ?? row.total) || amount + tax,
				currency: resolveFinancialCurrency(row.currency, fallbackCurrency),
			} satisfies ChargeLineItem;
		})
		.filter((item): item is ChargeLineItem => Boolean(item));
}

function parseInvoiceDetails(
	raw: unknown,
	meta: Record<string, unknown>,
): ChargesInvoiceDetails {
	const row = asRecord(raw) ?? {};
	const paymentDateRaw = asString(row.paymentDate ?? meta.paymentDate).trim();
	const paymentMethod =
		mapChargesPaymentMethodToApi(row.paymentMethod) || "APP";
	return {
		totalAmount: parseMoney(row.totalAmount),
		totalVat: parseMoney(row.totalVat),
		totalToPay: parseMoney(row.totalToPay),
		totalAmountDisplay: asString(row.totalAmount).trim(),
		totalVatDisplay: asString(row.totalVat).trim(),
		totalToPayDisplay: asString(row.totalToPay).trim(),
		paymentStatus:
			mapChargesPaymentStatusToApi(row.paymentStatus ?? meta.status) ||
			"OUTSTANDING",
		paymentStatusLabel: asString(
			row.paymentStatusLabel ?? meta.statusLabel,
		).trim(),
		paymentDate: formatShortDate(paymentDateRaw) || paymentDateRaw,
		paymentDateIso: toDateInputValue(paymentDateRaw),
		paymentMethod,
		paymentMethodLabel:
			asString(row.paymentMethodLabel).trim() ||
			mapChargesPaymentMethodLabel(paymentMethod),
		paymentReferenceId: asString(row.paymentReferenceId),
		partialPayment: parseMoney(row.partialPayment),
		remaining: parseMoney(row.remaining),
		remainingTax: parseOptionalMoney(row.remainingTax),
		remainingDisplay: asString(row.remainingDisplay).trim(),
	};
}

export function parseChargesUnitInvoice(
	raw: unknown,
	fallback: { propertyId: string; periodId: string; unitId: string },
): ChargesUnitDetail {
	const data = unwrapData(raw);
	const header = parseChargesPeriodUnitsHeader(data.header, fallback);
	const meta = asRecord(data.meta) ?? {};
	const invoiceActions = asRecord(data.actions) ?? {};
	const breakdown = asRecord(data.chargeBreakdown) ?? {};
	const owners = parseOwners(data.owners);
	const primaryOwner =
		owners.find((owner) => owner.isPrimary) ?? owners[0] ?? null;
	const unitCode =
		asString(data.unitNumber ?? data.unitCode).trim() ||
		primaryOwner?.unitNumber ||
		"—";
	const ownerName =
		asString(primaryOwner?.fullName).trim() ||
		asString(data.ownerName).trim() ||
		"—";
	const currency = resolveFinancialCurrency(data.currency);
	const invoiceDetails = parseInvoiceDetails(data.invoiceDetails, meta);
	const lineItems = parseLineItems(
		breakdown.items ?? data.chargeBreakdown,
		currency,
	);
	const invoiceDateRaw = asString(meta.invoiceDate).trim();
	const statusApi =
		asString(meta.status ?? invoiceDetails.paymentStatus)
			.trim()
			.toUpperCase() || "OUTSTANDING";
	const canEdit = asBoolean(
		invoiceActions.canEdit ?? header.canEdit,
		header.canEdit,
	);
	const canEditBill =
		isDraftServicePeriod(header.periodStatus) &&
		asBoolean(invoiceActions.canEditBill, canEdit);

	return {
		id: asString(data.id, fallback.unitId),
		propertyId: header.propertyId,
		periodId: header.servicePeriodId,
		breadcrumbUnitLabel: `${unitCode} - ${ownerName}`,
		invoiceNumber: asString(meta.invoiceNumber).trim() || "—",
		unitCode,
		currency,
		ownerName,
		ownerInitials:
			asString(primaryOwner?.initials).trim() ||
			initialsFromName(ownerName) ||
			"—",
		ownerPhone: asString(primaryOwner?.phone),
		ownerEmail: asString(primaryOwner?.email),
		unitDetail: [header.propertyName, unitCode].filter(Boolean).join(" · "),
		status: mapChargesUnitStatusFromApi(
			meta.status ?? meta.statusLabel ?? invoiceDetails.paymentStatus,
		),
		statusApi,
		statusLabel:
			asString(meta.statusLabel).trim() || invoiceDetails.paymentStatusLabel,
		servicePeriodLabel:
			asString(meta.servicePeriodLabel).trim() || header.periodLabel,
		invoiceDate: formatShortDate(invoiceDateRaw) || invoiceDateRaw || "—",
		invoiceDateIso: toDateInputValue(invoiceDateRaw),
		paymentDate: invoiceDetails.paymentDate || "—",
		paymentDateIso: invoiceDetails.paymentDateIso,
		owners,
		ownerDetails: parseOwnerDetails(data.ownerDetails),
		invoiceDetails,
		lineItems,
		subTotalAmountDisplay: asString(breakdown.subTotalAmount).trim(),
		subTotalTaxDisplay: asString(breakdown.subTotalTax).trim(),
		subTotalIncTaxDisplay: asString(breakdown.subTotalIncTax).trim(),
		emptyMessage: asString(breakdown.emptyMessage).trim(),
		totalAmount:
			invoiceDetails.totalToPay ||
			lineItems.reduce((sum, line) => sum + line.total, 0),
		actions: {
			canEdit,
			canEditBill,
			canEditPayment: asBoolean(invoiceActions.canEditPayment, canEdit),
			canDownloadCsv: asBoolean(invoiceActions.canDownloadCsv, true),
			canDownloadPdf: asBoolean(invoiceActions.canDownloadPdf, true),
		},
		header,
		updatedAt: asString(data.updatedAt).trim() || null,
	};
}

function roundMoney(value: number): number {
	return Math.round(value * 100) / 100;
}

function normalizeChargesPaymentStatus(status: string): string {
	return String(status ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
}

export function computeChargesInvoiceRemaining(
	status: string,
	totalToPay: number,
	partialPayment: number,
	currentRemaining: number,
): number {
	const normalized = normalizeChargesPaymentStatus(status);
	if (normalized === "SETTLED") return 0;
	if (normalized === "PARTIALLY_PAID") {
		const remaining = Number(totalToPay) - Number(partialPayment);
		if (!Number.isFinite(remaining)) return 0;
		return Math.max(0, roundMoney(remaining));
	}
	return currentRemaining;
}

export function computeChargesInvoicePartialPayment(
	status: string,
	totalToPay: number,
	currentPartialPayment: number,
): number {
	if (normalizeChargesPaymentStatus(status) === "SETTLED") {
		const paid = Number(totalToPay);
		return Number.isFinite(paid) ? Math.max(0, roundMoney(paid)) : 0;
	}
	return currentPartialPayment;
}

export function cloneChargesUnitDetail(
	detail: ChargesUnitDetail,
): ChargesUnitDetail {
	return structuredClone(detail);
}

export function buildUpdateChargesUnitInvoiceBody(
	detail: ChargesUnitDetail,
): UpdateChargesUnitInvoiceBody {
	const paymentStatus =
		mapChargesPaymentStatusToApi(
			detail.invoiceDetails.paymentStatus || detail.statusApi,
		) || "OUTSTANDING";
	const isDraftPeriod = isDraftServicePeriod(detail.header.periodStatus);
	const partialPayment = computeChargesInvoicePartialPayment(
		paymentStatus,
		detail.invoiceDetails.totalToPay,
		detail.invoiceDetails.partialPayment,
	);
	const invoiceDetails: ChargesInvoiceDetailsBody = {
		paymentStatus,
		paymentDate: detail.invoiceDetails.paymentDateIso,
		paymentMethod:
			mapChargesPaymentMethodToApi(detail.invoiceDetails.paymentMethod) ||
			"APP",
		paymentReferenceId: detail.invoiceDetails.paymentReferenceId,
		partialPayment,
		remaining: computeChargesInvoiceRemaining(
			paymentStatus,
			detail.invoiceDetails.totalToPay,
			partialPayment,
			detail.invoiceDetails.remaining,
		),
		remainingTax: detail.invoiceDetails.remainingTax,
	};
	if (isDraftPeriod) {
		invoiceDetails.totalAmount = detail.invoiceDetails.totalAmount;
		invoiceDetails.totalVat = detail.invoiceDetails.totalVat;
		invoiceDetails.totalToPay = detail.invoiceDetails.totalToPay;
	}
	const body: UpdateChargesUnitInvoiceBody = { invoiceDetails };
	if (isDraftPeriod) {
		body.chargeBreakdown = detail.lineItems.map((line) => ({
			id: line.id,
			category: line.category,
			description: line.description,
			amount: line.amount,
			tax: line.tax,
			currency: line.currency || detail.currency,
		}));
	}
	return body;
}
